import {
  mergeProps,
  Select,
  type SelectIconProps,
  type SelectItemProps,
  type SelectLabelProps,
  type SelectListProps,
  type SelectPopupProps,
  type SelectPositionerProps,
  type SelectScrollUpArrowProps,
  type SelectTriggerProps,
} from '@base-ui/react';
import type { ReactNode } from 'react';
import { IoChevronDownOutline } from 'react-icons/io5';
import commonStyles from '../styles/common.module.css';
import { FuuzeButton } from './fuuze-button.js';
import { FuuzeLoading } from './fuuze-loading.js';
import styles from './fuuze-select.module.css';

function Trigger(props: SelectTriggerProps) {
  const mergedProps = mergeProps<typeof Select.Trigger>(props, { className: styles.trigger });
  return <Select.Trigger render={<FuuzeButton outline />} {...mergedProps} />;
}

function Icon(props: SelectIconProps) {
  const mergedProps = mergeProps<typeof Select.Icon>(props, { className: styles.icon });
  return <Select.Icon {...mergedProps} />;
}

function Positioner(props: SelectPositionerProps) {
  const mergedProps = mergeProps<typeof Select.Positioner>(props, { className: styles.positioner });
  return <Select.Positioner {...mergedProps} />;
}

function Popup(props: SelectPopupProps) {
  const mergedProps = mergeProps<typeof Select.Popup>(props, { className: styles.popup });
  return <Select.Popup {...mergedProps} />;
}

function ScrollUpArrow(props: SelectScrollUpArrowProps) {
  const mergedProps = mergeProps<typeof Select.ScrollUpArrow>(props, { className: styles.scrollUpArrow });
  return <Select.ScrollUpArrow {...mergedProps} />;
}

function List(props: SelectListProps) {
  const mergedProps = mergeProps<typeof Select.List>(props, { className: styles.list });
  return <Select.List {...mergedProps} />;
}

function Item(props: SelectItemProps) {
  const mergedProps = mergeProps<typeof Select.Item>(props, { className: styles.item });
  return <Select.Item {...mergedProps} />;
}

function Label(props: SelectLabelProps) {
  const mergedProps = mergeProps<typeof Select.Label>(props, { className: styles.label });
  return <Select.Label {...mergedProps} />;
}

function GroupLabel(props: Select.GroupLabel.Props) {
  const mergedProps = mergeProps(props, { className: styles.groupLabel });
  return <Select.GroupLabel {...mergedProps} />;
}

function Group(props: Select.Group.Props) {
  const mergedProps = mergeProps(props, { className: styles.group });
  return <Select.Group {...mergedProps} />;
}

function Separator(props: Select.Separator.Props) {
  const mergedProps = mergeProps(props, { className: styles.separator });
  return <Select.Separator {...mergedProps} />;
}

function Value(props: Select.Value.Props) {
  const mergedProps = mergeProps(props, { className: styles.value });
  return <Select.Value {...mergedProps} />;
}

export const FuuzeSelect = {
  Arrow: Select.Arrow,
  Backdrop: Select.Backdrop,
  Group,
  GroupLabel,
  Icon,
  Item,
  ItemIndicator: Select.ItemIndicator,
  ItemText: Select.ItemText,
  Label,
  List,
  Popup,
  Portal: Select.Portal,
  Positioner,
  Root: Select.Root,
  ScrollDownArrow: Select.ScrollDownArrow,
  ScrollUpArrow,
  Separator,
  Trigger,
  Value,
};

type Item<Value> = { key: string; value: Value; label: string };
export type FuuzeSelectProps<Value> = {
  label?: string;
  loading?: boolean;
  PositionerProps?: Select.Positioner.Props;
  renderItem?: (option: Item<Value>) => ReactNode;
  RootProps: Omit<Select.Root.Props<Value>, 'items'> & { items: Item<Value>[] };
  ValueProps?: Select.Value.Props;
};

export function FuuzeSelectComponent<Value>({
  renderItem: renderItems,
  label,
  loading,
  PositionerProps,
  RootProps,
  ValueProps,
}: FuuzeSelectProps<Value>) {
  const item = RootProps.items.find(i => i.value === RootProps.value);

  return (
    <FuuzeSelect.Root {...RootProps} disabled={loading || RootProps.disabled}>
      {label && <FuuzeSelect.Label>{label}</FuuzeSelect.Label>}

      <FuuzeSelect.Trigger>
        <FuuzeSelect.Value
          {...ValueProps}
          className={commonStyles.limitTextTo1Line}
          style={{ textAlign: 'start', ...ValueProps?.style }}
          title={ValueProps?.title ?? item?.label}
        />
        {loading ? <FuuzeLoading $color='light' /> : <IoChevronDownOutline />}
      </FuuzeSelect.Trigger>

      <FuuzeSelect.Portal>
        <Positioner alignItemWithTrigger={false} sideOffset={8} {...PositionerProps}>
          <Popup>
            <ScrollUpArrow />
            <List>
              {RootProps.items.map(option => (
                <FuuzeSelect.Item key={option.key} value={option.value}>
                  {renderItems ? renderItems(option) : option.label}
                </FuuzeSelect.Item>
              ))}
            </List>
          </Popup>
        </Positioner>
      </FuuzeSelect.Portal>
    </FuuzeSelect.Root>
  );
}
